import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './App.css';
import './Components/Fonts/Font.css';
import HomeLoading from './Loading/HomeLoading';
import { useTelegramLogin } from './hooks/auth/useTelegramLogin';
import StorePanelStation from './pages/StorePanel/StorePanelStation/StorePanelStation';
import StoreForm from './pages/StorePanel/StoreForm/StoreForm';
import StoreEmployee from './pages/StorePanel/StoreEmployee/StoreEmployee';
import Reports from './pages/Buyer/Reports/Reports';
import CollebrationRequest from './pages/Buyer/CollebrationRequest/CollebrationRequest';
import { ToastContainer } from 'react-toastify';
import HomePage from './pages/Store/HomePage/HomePage';
import Shop from './pages/Store/Shop/Shop';
import ShopBasket from './pages/Store/ShopBasket/ShopBasket';
import ShopPayment from './pages/Store/ShopPayment/ShopPayment';
import Register from './pages/Buyer/Register/Register';
import Invoices from './pages/Buyer/Invoices/Invoices';
import { useTelegram } from './hooks/auth/useTelegram';
import ShopLoading from './pages/Store/ShopLoading/ShopLoading';
import StoreProductsGroup from './pages/StorePanel/StoreProducts/StoreProductsGroup/StoreProductsGroup';
import ProductDefinition from './pages/StorePanel/StoreProducts/ProductDefinition/ProductDefinition';
import OrdersPage from './pages/Buyer/OrdersPage/OrdersPage';
import ShopDelivery from './pages/Store/ShopDelivery/ShopDelivery';
import PaymentResult from './pages/PaymentResult/PaymentResult';
import 'react-toastify/dist/ReactToastify.css';
import { BuyerInvoice } from './pages/Print/BuyerInvoice/BuyerInvoice';
import SellerInvoice from './pages/Print/SellerInvoice/SellerInvoice';
import ProtectedRoute from './Components/ProtectedRoute';
import config from './config';
import MobileLogin from './pages/Auth/MobileLogin/MobileLogin';

// 🔹 استخراج start_param از تمام مبادی ممکن تلگرام
export const getTelegramStartParam = () => {
  const directParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (directParam) return String(directParam).trim();

  const searchParams = new URLSearchParams(window.location.search);
  const searchParam = searchParams.get('tgWebAppStartParam') || searchParams.get('startapp') || searchParams.get('start_param');
  if (searchParam) return String(searchParam).trim();

  if (window.location.hash) {
    const rawHash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
    const hashParams = new URLSearchParams(rawHash);
    const hashParam = hashParams.get('tgWebAppStartParam') || hashParams.get('start_param') || hashParams.get('startapp');
    if (hashParam) return String(hashParam).trim();

    const webAppData = hashParams.get('tgWebAppData');
    if (webAppData) {
      const innerParams = new URLSearchParams(webAppData);
      const innerStartParam = innerParams.get('start_param');
      if (innerStartParam) return String(innerStartParam).trim();
    }
  }

  return null;
};

// 🔹 تابع تجزیه مطمئن پارامتر به { shop, product }
export const extractShopAndProduct = (raw) => {
  if (!raw) return null;

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const s = raw.shop || raw.sId || raw.storeId;
    const p = raw.product || raw.pId || raw.commodityId;
    if (s) return { shop: String(s), product: p ? String(p) : null };
  }

  if (Array.isArray(raw)) {
    const sIdx = raw.indexOf('s');
    const cIdx = raw.indexOf('c');
    const shopVal = sIdx !== -1 ? raw[sIdx + 1] : null;
    const prodVal = cIdx !== -1 ? raw[cIdx + 1] : null;
    if (shopVal) return { shop: String(shopVal), product: prodVal ? String(prodVal) : null };
  }

  if (typeof raw === 'string') {
    const parts = raw.split('_');
    const sIdx = parts.indexOf('s');
    const cIdx = parts.indexOf('c');
    const shopVal = sIdx !== -1 ? parts[sIdx + 1] : (parts.length > 0 && !isNaN(parts[0]) ? parts[0] : null);
    const prodVal = cIdx !== -1 ? parts[cIdx + 1] : null;
    if (shopVal) return { shop: String(shopVal), product: prodVal ? String(prodVal) : null };
  }

  return null;
};

function InitialRedirect({ to, state, onComplete }) {
  useEffect(() => {
    onComplete(true);
  }, [onComplete]);

  return <Navigate to={to} state={state} replace />;
}

function App() {
  const [initialRedirectDone, setInitialRedirectDone] = useState(false);
  const [userExitedShop, setUserExitedShop] = useState(false); // 👈 مانع از بازگشت ناخواسته به فروشگاه بعد از خروج

  const { ChatId, params } = useTelegram();
  useTelegramLogin(ChatId);

  const toastRoot = document.getElementById('toast-root');
  const startParam = useMemo(() => getTelegramStartParam(), []);

  useEffect(() => {
    const handleProfileUpdate = () => {
      // رفرش وضعیت لاگین
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, []);

  useEffect(() => {
    const app = window.Telegram?.WebApp;
    if (app) {
      app.ready();
      app.expand();
      try { if (app.requestFullscreen) app.requestFullscreen(); } catch (e) { }
      try { if (app.disableVerticalSwipes) app.disableVerticalSwipes(); } catch (e) { }
    }
  }, []);

  // محاسبه پارامترهای فعال
  const rawShopParams = useMemo(() => {
    const fromHook = extractShopAndProduct(params);
    if (fromHook) return fromHook;

    const fromStartParam = extractShopAndProduct(startParam);
    if (fromStartParam) return fromStartParam;

    const directStart = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    const fromDirect = extractShopAndProduct(directStart);
    if (fromDirect) return fromDirect;

    const urlParams = new URLSearchParams(window.location.search);
    const sId = urlParams.get('sId');
    const pId = urlParams.get('pId') || urlParams.get('commodityId');
    if (sId) {
      return { shop: String(sId), product: pId ? String(pId) : null };
    }

    return null;
  }, [params, startParam]);

  // اگر کاربر دکمه خروج را زد، دیگر پارامتر اولیه را به عنوان لود خودکار تلقی نکن
  const activeShopParams = userExitedShop ? null : rawShopParams;

  const handleClearShopParams = useCallback(() => {
    setUserExitedShop(true);
    // پاک کردن کوئری استرینگ‌ها از آدرس بدون رفرش صفحه
    if (window.history?.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const handleFocusIn = (e) => {
      const target = e.target;
      const tagName = target?.tagName;

      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
        if (
          tagName === 'INPUT' &&
          ['checkbox', 'radio', 'file', 'submit', 'button', 'reset', 'color', 'hidden'].includes(target.type)
        ) {
          return;
        }

        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  const targetRedirect = useMemo(() => {
    if (!startParam) return null;
    const low = String(startParam).toLowerCase();
    return config.notification_redirects?.[low] || null;
  }, [startParam]);

  return (
    <>
      {toastRoot && createPortal(
        <ToastContainer
          position="top-center"
          autoClose={3000}
          rtl
          newestOnTop
          style={{ zIndex: 2147483647 }}
        />,
        toastRoot
      )}

      <div className='App'>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                (!initialRedirectDone && targetRedirect) ? (
                  <InitialRedirect
                    to={targetRedirect.to}
                    state={targetRedirect.state}
                    onComplete={setInitialRedirectDone}
                  />
                ) : activeShopParams ? (
                  <Shop params={activeShopParams} onExitCallback={handleClearShopParams} />
                ) : (
                  <Navigate to="/HomePage" replace />
                )
              }
            />

            <Route path="/login" element={<MobileLogin />} />
            <Route path="/payment" element={<PaymentResult />} />
            <Route exact path="/Shop" element={<Shop params={activeShopParams} onExitCallback={handleClearShopParams} />} />
            <Route exact path="/HomePage" element={<HomePage />} />
            <Route path="/Register" element={<ProtectedRoute><Register /></ProtectedRoute>} />
            <Route path="/ShopBasket" element={<ShopBasket />} />
            <Route path="/StorePanelStation" element={<StorePanelStation />} />

            {/* روت‌های محافظت‌شده */}
            <Route path="/StoreForm" element={<ProtectedRoute><StoreForm /></ProtectedRoute>} />
            <Route path="/StoreEmployee" element={<ProtectedRoute><StoreEmployee /></ProtectedRoute>} />
            <Route path="/ShopPayment" element={<ProtectedRoute><ShopPayment /></ProtectedRoute>} />
            <Route path="/ShopDelivery" element={<ProtectedRoute><ShopDelivery /></ProtectedRoute>} />
            <Route path="/Report" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
            <Route path="/CollebrationRequest" element={<ProtectedRoute><CollebrationRequest /></ProtectedRoute>} />
            <Route path="/group" element={<ProtectedRoute><StoreProductsGroup /></ProtectedRoute>} />

            <Route path="/ProductDefinition" element={<ProductDefinition />} />

            <Route path="/app" element={<ShopLoading />} />
            <Route path="/loading" element={<ShopLoading />} />
            <Route path="/:param" element={<ShopLoading />} />

            <Route path="/print/buyer-invoice" element={<BuyerInvoice />} />
            <Route path="/print/seller-invoice" element={<SellerInvoice />} />
          </Routes>
        </BrowserRouter>
      </div>
    </>
  );
}

export default App;
